import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Drawer, Row } from "antd";
import { BsBoxes } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import {
    updateDrilldownFilters,
    resetRevstackDrilldownWidgetData,
    updateRevstackDrilldownWidgetData,
    updateAiInsightsPayloadData,
} from "@features/revstackWidgetsSlice";
import {
    useGetDashboardConfig,
    useGetRevstackDDTabsConfig,
    useRevstackStreamData,
} from "@hooks/AllCustomHooks";
import {
    CA_DD_MODULES,
    DASHBOARD_TYPES,
    RevstackCASectionIds,
    IRevstackWidgetData,
    RevstackDashboardSubModules,
} from "@ts/common-types";
import { ALL_OPTIONS, CatAnalyticsSubCatDrillDownConfig } from "@ts/constants";
import RevstackDrilldownDrawerHeader from "./RevstackDrilldownDrawerHeader";

import { getRevstackSelectedFilterOptions } from "@/ts/revstack/filters";
import { getActiveModule } from "@/ts/utils";
import { trendsClickHandler } from "@/ts/revstack/constants";

interface IRevstackDrilldownDrawer {
    isOpen: boolean;
    activeSectionId: RevstackCASectionIds | null;
    onClose: () => void;
    config: any;
    title: string;
}

const RevstackDrilldownDrawer: React.FC<IRevstackDrilldownDrawer> = ({
    isOpen,
    activeSectionId,
    onClose,
    config,
    title = "Drill Down"
}) => {
    const activeModule = getActiveModule().activeModule;
    const initialSubcategoryValueRef = useRef<any>(null);
    const dispatch = useDispatch();
    const drilldownConfig = useGetDashboardConfig(
        activeModule,
        DASHBOARD_TYPES.DRILL_DOWN,
        undefined,
        activeSectionId
    ).widgetConfig;
    const revstackWidgetData = useSelector(
        (state: {
            revstackWidgetData: IRevstackWidgetData;
        }) => state.revstackWidgetData
    );
    const drilldownFilters = useSelector(
        (state: { drilldownFilters: { value: any } }) =>
            state.drilldownFilters.value
    );
    
    const [activeTab, setActiveTab] = useState(CA_DD_MODULES.OVERVIEW);

    const drilldownConfigWithoutBanner = drilldownConfig?.filter((config: any) => config.sectionId !== RevstackCASectionIds.BANNER) || []

    const primaryFilter = useMemo(() => {
        const filterKey = drilldownConfigWithoutBanner[0]?.config?.header?.components?.find((component: any) => component.section === "main")?.elements?.[0]?.filterKey || "";
        const filterValue = drilldownFilters.selected?.[filterKey];
        return {
            filterValue,
            filterKey,
        }
    }, [drilldownFilters.selected, drilldownConfigWithoutBanner]);

    const secondaryFilter = useMemo(() => {
        const filterKey = drilldownConfigWithoutBanner[0]?.config?.header?.components?.find((component: any) => component.section === "filters")?.elements?.[0]?.filterKey || "";
        const filterValue = drilldownFilters.selected?.[filterKey];
        return {
            filterKey,
            filterValue,
        }
    }, [drilldownFilters.selected, drilldownConfigWithoutBanner]);

    const onPrimaryFilterChange = (value: any) => {
        dispatch(
            updateDrilldownFilters({
                selected: {
                    ...drilldownFilters?.selected,
                    [primaryFilter?.filterKey]:
                        value === ALL_OPTIONS.value
                            ? [ALL_OPTIONS.value]
                            : [parseInt(value, 10)],
                },
            })
        );
    };

    const onSecondaryFilterChange = (value: any) => {
        dispatch(
            updateDrilldownFilters({
                selected: {
                    ...drilldownFilters?.selected,
                    [secondaryFilter?.filterKey]:
                        value === ALL_OPTIONS.value
                            ? [ALL_OPTIONS.value]
                            : [value],
                },
            })
        );
    };

    useEffect(() => {
        if (isOpen && initialSubcategoryValueRef.current === null) {
            initialSubcategoryValueRef.current =
                primaryFilter?.filterValue?.length > 0
                    ? primaryFilter?.filterValue?.[0]
                    : ALL_OPTIONS.value;
        }
    }, [isOpen, primaryFilter?.filterValue]);

    const handleClose = () => {
        if (initialSubcategoryValueRef.current !== null) {
            onPrimaryFilterChange(initialSubcategoryValueRef.current);
            initialSubcategoryValueRef.current = null;
        }
        onClose();
    };

    const filterReducer = (
        state: any,
        action: {
            tab: RevstackDashboardSubModules;
            filters: Partial<any[CA_DD_MODULES]>;
        }
    ): any => {
        return {
            ...state,
            [action.tab]: {
                ...state[action.tab],
                filters: {
                    ...state[action.tab].filters,
                    ...action.filters,
                },
            },
        };
    };

    const [filterState, filterDispatch] = useReducer(
        filterReducer,
        CatAnalyticsSubCatDrillDownConfig
    );

    const primaryFilterOptions = useMemo(
        () =>(drilldownFilters?.options?.[primaryFilter?.filterKey]),
        [drilldownFilters?.options?.[primaryFilter?.filterKey]]
    );

    const secondaryFilterOptions = useMemo(
        () =>
            (drilldownFilters?.options?.[secondaryFilter?.filterKey]),
        [drilldownFilters?.options?.[secondaryFilter?.filterKey]]
    );

    const { getRevstackWidgetStreamData } = useRevstackStreamData({
        config: drilldownConfig,
        streamConfig: {
            resetWidgetDataAction: resetRevstackDrilldownWidgetData,
            updateWidgetDataAction: updateRevstackDrilldownWidgetData,
            updateAiInsightsAction: updateAiInsightsPayloadData,
            aiInsightsKey: "drilldown",
        },
        overviewTabKey: RevstackDashboardSubModules.OVERVIEW,
    });

    useEffect(() => {
        if (isOpen) {
            getRevstackWidgetStreamData();
        }
    }, [isOpen, drilldownFilters]);

    const { bannerConfig, nonBannerConfig } = useMemo(() => {
        return drilldownConfig.reduce(
            (acc: any, widgetConfig: any) => {
                if (widgetConfig.sectionId === RevstackCASectionIds.BANNER) {
                    acc.bannerConfig.push(widgetConfig);
                } else {
                    acc.nonBannerConfig.push(widgetConfig);
                }
                return acc;
            },
            { bannerConfig: [], nonBannerConfig: [] }
        );
    }, [drilldownConfig]);

    const filteredBannerConfig = useMemo(() => {
        return bannerConfig
            .map((widgetConfig: any, originalIndex: number) => ({
                ...widgetConfig,
                stableKey:
                    widgetConfig.widgets?.[0]?.identifier ||
                    `${widgetConfig.sectionId}-${originalIndex}`,
            }))
            .filter((widgetConfig: any) =>
                widgetConfig.widgets?.some(
                    (widget: any) => widget.relativeTabKey === activeTab
                )
            );
    }, [bannerConfig, activeTab]);

    const tabItems = useGetRevstackDDTabsConfig({
        drilldownConfig: nonBannerConfig,
        activeTab,
        filterState,
        filterDispatch,
        drilldownFilters,
        widgetData: revstackWidgetData.drilldown,
        layoutType: "drilldown",
    });

    // Generate items and tabContentMap without useMemo - let React handle re-renders naturally
    const items = tabItems.map(({ key, label, disabled }) => ({
        key,
        label,
        disabled,
    }));

    const tabContentMap = tabItems.reduce((acc, { key, children }) => {
        acc[key] = children;
        return acc;
    }, {} as Record<string, React.ReactNode>);

    const selectedOption = primaryFilterOptions?.find((option: any) =>
        primaryFilter?.filterValue?.includes(option.value)
    );

    const selectedSecondaryOption = secondaryFilterOptions?.find((option: any) =>
        secondaryFilter?.filterValue?.includes(option.value)
    );

    const selectedPrimaryFilterLabel = selectedOption?.label;
    const selectedPrimaryFilterSubLabel = selectedOption?.subLabel;
    const selectedSecondaryLabel = selectedSecondaryOption?.label;
    const selectedFilterOptions = getRevstackSelectedFilterOptions();

    return (
            <Drawer
                open={isOpen}
                onClose={handleClose}
                width="75%"
                footer={null}
                className="revstack-dashboard-scope"
                title={
                    <RevstackDrilldownDrawerHeader
                        title={title}
                        primaryFilterSubLabel={selectedPrimaryFilterSubLabel}
                        config={drilldownConfigWithoutBanner?.[0]?.config || {}}
                        titleIcon={<BsBoxes size={16} />}
                        {...{primaryFilterOptions,
                            secondaryFilterOptions,
                            onPrimaryFilterChange,
                            onSecondaryFilterChange,
                            selectedPrimaryFilterLabel,
                            selectedSecondaryLabel,
                            activeTab, 
                            items
                        }}
                        setActiveTab={(activeKey) =>
                            setActiveTab(activeKey as CA_DD_MODULES)
                        }
                        secondaryFilterValue={secondaryFilter?.filterValue}
                        primaryFilterValue={primaryFilter?.filterValue}
                        titlePills={config?.header?.titlePills || []}
                        onTrendsViewClick={() => {
                            trendsClickHandler({
                                widgetConfig: drilldownConfig?.[0],
                                titlePills: [selectedPrimaryFilterLabel],
                                selectedFilterOptions,
                                ...drilldownConfig?.[0]?.config?.trendsFiltersConfig,
                                sectionId: activeSectionId,
                            });
                        }
                        }
                    />
                }
                closable={true}
                styles={{
                    body: { padding: 0 },
                }}
            >
                {filteredBannerConfig?.length > 0 && (
                    <Row className="w-100">
                        {filteredBannerConfig.map((widgetConfig: any) => {
                            const WidgetComponent = widgetConfig.widget;
                            return (
                                <div
                                    key={widgetConfig.stableKey}
                                    className="w-100"
                                >
                                    <WidgetComponent
                                        widgetConfig={widgetConfig}
                                        widgetData={
                                            revstackWidgetData.drilldown
                                        }
                                        title={widgetConfig.title}
                                    />
                                </div>
                            );
                        })}
                    </Row>
                )}
                <Row
                    className="w-100 revstack-layout-whitespace p-4"
                    justify="space-between"
                    align="middle"
                >
                    {tabContentMap[activeTab] || null}
                </Row>
            </Drawer>
    );
};

export default RevstackDrilldownDrawer;